import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { apiAuth } from "@/lib/api";
import { StatusBadge, PIPELINE_INVESTORS } from "@/components/admin/StatusBadge";

const PAGE_SIZES = [25, 50, 100];

export default function AdminInvestorsList() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const limit = Number(params.get("limit") || 25);
  const status = params.get("status") || "";
  const q = params.get("q") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [draftQ, setDraftQ] = useState(q);

  const query = useMemo(() => {
    const s = new URLSearchParams();
    s.set("page", String(page));
    s.set("limit", String(limit));
    if (status) s.set("status", status);
    if (q) s.set("q", q);
    return s.toString();
  }, [page, limit, status, q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiAuth.get(`/admin/investors?${query}`).then(({ data }) => {
      if (!cancelled) { setData(data); setLoading(false); }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const updateParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v == null) next.delete(k); else next.set(k, String(v));
    next.set("page", "1");
    setParams(next);
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="page-admin-investors" className="flex flex-col gap-5">
      <div className="bg-white border border-sandBeige rounded-3xl p-4 flex flex-wrap items-center gap-3">
        <form
          onSubmit={(e) => { e.preventDefault(); updateParam("q", draftQ.trim() || ""); }}
          className="flex items-center gap-2 flex-1 min-w-[200px]"
        >
          <div className="flex items-center gap-2 bg-porcelain rounded-2xl border border-sandBeige px-3 py-2 flex-1">
            <Search size={14} className="text-warmGrey" />
            <input
              className="bg-transparent outline-none text-sm flex-1"
              placeholder="Suche Organisation / Thesis …"
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              data-testid="investors-search"
            />
          </div>
        </form>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="wit-pill bg-white border-sandBeige hover:border-raspberry text-cocoa"
          data-testid="investors-toggle-filters"
        >
          <SlidersHorizontal size={14} className="inline mr-1.5" /> Filter
        </button>
      </div>

      {showFilters && (
        <div className="bg-white border border-sandBeige rounded-3xl p-5 grid sm:grid-cols-3 gap-4" data-testid="investors-filters">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-cocoa">Status</span>
            <select
              className="wit-input"
              value={status}
              onChange={(e) => updateParam("status", e.target.value)}
              data-testid="filter-status"
            >
              <option value="">Alle</option>
              {PIPELINE_INVESTORS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
      )}

      <div className="bg-white border border-sandBeige rounded-3xl overflow-hidden">
        <table className="w-full hidden md:table">
          <thead className="bg-porcelain text-warmGrey">
            <tr>
              <th className="text-left text-xs font-medium px-5 py-3">Name</th>
              <th className="text-left text-xs font-medium px-4 py-3">Organisation</th>
              <th className="text-left text-xs font-medium px-4 py-3">Typ</th>
              <th className="text-left text-xs font-medium px-4 py-3">Stage</th>
              <th className="text-left text-xs font-medium px-4 py-3">Ticket</th>
              <th className="text-left text-xs font-medium px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sandBeige/60">
            {loading && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-warmGrey">Lade …</td></tr>
            )}
            {!loading && data?.items?.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-warmGrey">Keine Treffer.</td></tr>
            )}
            {data?.items?.map((inv) => (
              <tr key={inv.id} className="hover:bg-ivory/60 transition-colors">
                <td className="px-5 py-3.5">
                  <Link to={`/admin/investors/${inv.id}`} className="font-medium text-cocoa hover:text-raspberry" data-testid={`row-investor-${inv.id}`}>
                    {inv.contact ? `${inv.contact.first_name} ${inv.contact.last_name}` : "—"}
                  </Link>
                  <p className="text-xs text-warmGrey truncate">{inv.contact?.email}</p>
                </td>
                <td className="px-4 py-3.5 text-sm text-cocoa">{inv.organization_name}</td>
                <td className="px-4 py-3.5 text-xs text-cocoa">{(inv.investor_types || []).slice(0,2).join(", ")}</td>
                <td className="px-4 py-3.5 text-xs text-cocoa">{(inv.stage_focus || []).slice(0,2).join(", ")}</td>
                <td className="px-4 py-3.5 text-xs text-cocoa">
                  {Math.round(inv.ticket_min/1000)}k – {Math.round(inv.ticket_max/1000)}k {inv.currency}
                </td>
                <td className="px-4 py-3.5"><StatusBadge value={inv.status} size="sm" /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="md:hidden divide-y divide-sandBeige/60">
          {!loading && data?.items?.length === 0 && (
            <p className="px-5 py-10 text-center text-warmGrey">Keine Treffer.</p>
          )}
          {data?.items?.map((inv) => (
            <Link
              key={inv.id}
              to={`/admin/investors/${inv.id}`}
              className="block px-5 py-4 hover:bg-ivory/60 transition-colors"
              data-testid={`card-investor-${inv.id}`}
            >
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-cocoa truncate">
                    {inv.contact ? `${inv.contact.first_name} ${inv.contact.last_name}` : "—"}
                  </p>
                  <p className="text-xs text-warmGrey truncate">{inv.organization_name}</p>
                  <p className="text-xs text-warmGrey mt-1">
                    {(inv.investor_types || []).slice(0,2).join(", ")} · {Math.round(inv.ticket_min/1000)}k–{Math.round(inv.ticket_max/1000)}k {inv.currency}
                  </p>
                </div>
                <StatusBadge value={inv.status} size="sm" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-warmGrey">
        <div className="flex items-center gap-2">
          <span>Pro Seite:</span>
          {PAGE_SIZES.map((n) => (
            <button
              key={n} type="button"
              onClick={() => updateParam("limit", n)}
              className={`wit-pill text-xs ${limit === n ? "bg-raspberry text-white border-raspberry" : "bg-white border-sandBeige text-cocoa"}`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span>{total} Treffer</span>
          <button type="button" disabled={page <= 1} onClick={() => updateParam("page", page - 1)} className="wit-pill bg-white border-sandBeige text-cocoa disabled:opacity-40">‹ Prev</button>
          <span className="text-cocoa">{page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => updateParam("page", page + 1)} className="wit-pill bg-white border-sandBeige text-cocoa disabled:opacity-40">Next ›</button>
        </div>
      </div>
    </div>
  );
}
